import _ from "lodash"
import moment from "moment"
import React from "react"

const getURLParams = path => {
  let urlParams = {}
  let urlPath = !_.isEmpty(path) ? path : window.location.href

  urlPath.replace(/[?&]+([^=&]+)=([^&]*)/gi, function (m, key, value) {
    urlParams[key] = value
  })
  return urlParams
}

const isDevEnvironment = () => {
  return process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test"
}

const isPlural = item => {
  return item > 1 ? "s" : ""
}

// Return the current local date with no associated time data
// in ISO8601 format
export const getLocalDate = () => new Date().toISOString().split("T")[0] + "T00:00:00.000Z"

// Taken from https://stackoverflow.com/questions/2090551/parse-query-string-in-javascript.
// Turns a URL query string into an object, ie `parseQuery(location.search)`
//
// Typically, you'll want to pass location.search as args.
export const parseQuery = queryString => {
  let query = {}
  let keyval, rawValue, convertedVal
  const pairs = (queryString[0] === "?" ? queryString.substr(1) : queryString).split("&")

  for (const pair of pairs) {
    keyval = pair.split("=")
    rawValue = decodeURIComponent(keyval[1] || "")

    // Try to convert to an array
    convertedVal = rawValue.split(",").length > 1 ? rawValue.split(",") : rawValue

    // Try to convert all values to integers
    const tryInt = val => (parseInt(val) ? parseInt(val) : val)
    query[decodeURIComponent(keyval[0])] = _.isArray(convertedVal)
      ? convertedVal.map(tryInt)
      : tryInt(convertedVal)
  }

  return query
}
//takes in a JS date object and returns a numeric date string, like '7/2/2021'
//we add one to month because getUTCMonth returns zero-indexed for some reason
const getNumericDateString = DateObject => {
  const month = (DateObject.getUTCMonth() + 1).toString()
  const day = DateObject.getUTCDate().toString()
  const year = DateObject.getUTCFullYear().toString()
  const dayOfWeek = DateObject.getUTCDay()
  let dayOfWeekString

  switch (dayOfWeek) {
    case 0:
      dayOfWeekString = "Sunday, "
      break
    case 1:
      dayOfWeekString = "Monday, "
      break
    case 2:
      dayOfWeekString = "Tuesday, "
      break
    case 3:
      dayOfWeekString = "Wednesday, "
      break
    case 4:
      dayOfWeekString = "Thursday, "
      break
    case 5:
      dayOfWeekString = "Friday, "
      break
    case 6:
      dayOfWeekString = "Saturday, "
      break
  }

  return dayOfWeekString + month + "/" + day + "/" + year
}

// Takes an object and a key mapping and converts the objects keys
// to the specified mapping. Example:
// obj = { 'id': 'foo', 'name': 'bar' }
// mapping = { 'id': 'value', 'name': 'label' }
// result = { 'value': 'foo', 'label': 'bar' }
const transformKeys = (obj, mapping) => {
  let newObj = Object.assign(obj, {})

  _.each(mapping, (newKey, oldKey) => {
    newObj[newKey] = newObj[oldKey]
    delete newObj[oldKey]
  })

  return newObj
}

/*
 * Convert an array of objects to a single object
 */
const objectArrayToObject = array => _.reduce(array, (obj, acc) => Object.assign(acc || {}, obj))

/*
 * % is a remainder operator, not a modulus operator.
 */
const mod = (n, m) => ((n % m) + m) % m

/* Removes the given value from the list if the value is present in the list. Otherwise, the value is added to the list.
 * Returs a new array; the given array is NOT modified.
 * */
const toggleValueInList = (value, list) => {
  const existingValue = _.find(list, existingValue => existingValue === value)
  let newList = Object.assign([], list)

  if (existingValue) {
    _.remove(newList, value => value === existingValue)
  } else {
    newList.push(value)
  }

  return newList
}

const utcISOStringToLocalISOString = timestamp => {
  let subseconds = new Date(timestamp).toISOString().split("T")[1].split(".")[1]
  let time = new Date(timestamp).toISOString().split("T")[1].split(".")[0].split(":")

  // Get hour difference between UTC and local. Positive for zones like
  // US EST (-4 hours) and negative for zones like Australia EST (+10 hours)
  const timezoneHourOffset = new Date().getTimezoneOffset() / 60

  // Take the difference of the UTC and local hour value and take the modulus
  // so that it always stays in the [0..23] range
  const adjustedHour = time[0] - timezoneHourOffset

  let date = new Date(timestamp).toISOString().split("T")[0].split("-")

  // If the local timezone is ahead enough such that the adjustedHour is 24+,
  // then it's already the next day in this local zone. Increment the day by 1.
  //
  // If adjustedHour is negative, it's still the previous day in the local
  // time zone. Decrement the day by 1.
  if (adjustedHour >= 24) {
    date[2] = date[2] + 1
    time[0] = adjustedHour - 24
  } else if (adjustedHour < 0) {
    date[2] = date[2] - 1
    time[0] = 24 + adjustedHour
  }

  // Ensure each hour/minute/second component has 2 digits
  date = _.map(date, d => (d.toString().length === 1 ? "0" + d : d.toString()))

  // Re-create the time part of the ISO string
  time = time.join(":") + "." + subseconds

  // Re-create the date part of the ISO string
  date = date.join("-")

  return date + "T" + time
}

const alphabetizeByKey = object => {
  const groupArray = _.map(object, (value, key) => ({ key: key, value: value }))
  let mergedObj = {}

  _.each(_.orderBy(groupArray, "key"), obj => {
    mergedObj[obj.key] = obj.value
  })

  return mergedObj
}

const executeIfNotEqual = (value1, value2, func) => {
  if (value1 !== value2) {
    func()
  }
}

// Returns a 2-dimensional array of all of the dates that need to be displayed for
// the currently-selected month
// Subtract() will modify the existing moment, so there needs to be a second one for subtracting
// and the original var will be incremented in the next stem
const getDayArray = (currentMoment, selectedDate = undefined) => {
  const todayString = moment().format("MM-DD-YYYY")
  const beginningOfMonth = moment(
    [currentMoment.format("M"), 1, currentMoment.format("YYYY")].join("/")
  )
  const beginningOfMonthForSubtracting = moment(
    [currentMoment.format("M"), 1, currentMoment.format("YYYY")].join("/")
  )
  const startingDayOfTheWeek = beginningOfMonth.format("d")
  const selectionString = _.isEmpty(selectedDate)
    ? undefined
    : moment(selectedDate).format("MM-DD-YYYY")

  // Function for building the necessary data for representing a single grid item on the calendar
  const getDateObjectFor = momentObj => ({
    number: momentObj.format("D"),
    isoString: momentObj.utc().startOf("day").toISOString(), //written to remove timezone offset
    isToday: momentObj.format("MM-DD-YYYY") == todayString,
    isSelected: _.isEmpty(selectionString)
      ? false
      : momentObj.utc().startOf("day").format("MM-DD-YYYY") == selectionString,
    isWeekday: [1, 2, 3, 4, 5].includes(parseInt(momentObj.format("d"))),
    isCurMonth: parseInt(momentObj.format("M")) === parseInt(currentMoment.format("M"))
  })

  // Build list of days from the previous month
  let previousMonthsDays = []
  for (var i = startingDayOfTheWeek; i > 0; i--) {
    previousMonthsDays.push(getDateObjectFor(beginningOfMonthForSubtracting.subtract(1, "days")))
  }

  // Finish the first row of days
  let firstRow = previousMonthsDays.reverse()
  firstRow.push(getDateObjectFor(beginningOfMonth))
  for (var i = firstRow.length; i < 7; i++) {
    firstRow.push(getDateObjectFor(beginningOfMonth.add(1, "days")))
  }

  // Build the remaining rows of the calendar
  let dayArray = [firstRow]
  for (var rowIndex = 1; rowIndex < 6; rowIndex++) {
    dayArray[rowIndex] = []
    for (var colIndex = 0; colIndex < 7; colIndex++) {
      dayArray[rowIndex][colIndex] = getDateObjectFor(beginningOfMonth.add(1, "days"))
    }
  }
  return dayArray
}

//takes a collection of  quests and an collection of every quest within a sequence, and returns an array of quests with quest_type.
const defineQuestTypes = (quests, quest_sequences) => {
  if (_.isEmpty(quests) || _.isEmpty(quest_sequences)) {
    return
  }
  var output_quests = quests
  var flat_quest_sequences = _.flatMap(quest_sequences)
  _.map(output_quests, quest => {
    var is_sequence_quest = false
    for (var i = 0; i < flat_quest_sequences.length; i++) {
      if (quest.id === flat_quest_sequences[i].id) {
        is_sequence_quest = true
        break
      }
    }
    if (is_sequence_quest) quest.quest_type = "sequence-quest"
    else if (quest.ngss_enrichment) quest.quest_type = "enrichment-quest"
    else quest.quest_type = "practice-quest"
  })
  return output_quests
}

//formats all of the keys in a plain object to camelCase
const formatKeysToCamelCase = object => {
  let newObject = {}
  _.map(Object.keys(object), key => (newObject[_.camelCase(key)] = object[key]))
  return newObject
}
//specific usage of the above function relating to student groupings. only use when formateKeysToCamelCase is also imported
const formatStudentKeysToCamelCase = studentGrouping => {
  _.map(studentGrouping, course =>
    _.map(course.sections, section =>
      _.map(section.students, student => {
        student.profile = formatKeysToCamelCase(student.profile)
      })
    )
  )
  return studentGrouping
}

//Converts the notifications objects from upsert_assignments into an array of messages to be displayed on the assignment popup
const getNotificationMessages = notificationObjects => {
  let notificationArray = []
  if (_.isEmpty(notificationObjects)) {
    return null
  }

  notificationArray = _.map(notificationObjects, object => {
    if (_.isEmpty(object.notification_type) && !_.isNumber(object.notification_type)) {
      console.log("a notification object was passed with no notification type!")
      return "Something went wrong!"
    } else {
      switch (object.notification_type) {
        case 1:
          if (_.isEmpty(object.details)) {
            console.log("a notification object was passed with no details")
            return "Something went wrong!"
          } else {
            const studentNames = _.map(object.details.student_names, name => {
              if (_.isString(name)) {
                return name
              }
            }).filter(element => {
              return element != null
            })

            if (_.isEmpty(studentNames)) {
              console.log("a notification object was passed with no valid student names")
              return "Something went wrong!"
            }

            let timestamp = "undefined"

            if (
              _.isEmpty(object.details.timestamp) ||
              !moment(object.details.timestamp).isValid()
            ) {
              console.log("a notification object was passed with no valid timestamp")
              return "Something went wrong!"
            } else {
              timestamp = moment(object.details.timestamp).format("MM/DD/YYYY")
            }

            return (
              "No new assignment(s) created for " +
              studentNames.join(", ") +
              ", because their class was already assigned to this content queued on " +
              timestamp
            )
          }
        case 2:
          if (_.isEmpty(object.details)) {
            console.log("a notification object was passed with no details")
            return "Something went wrong!"
          } else {
            const studentNames = _.map(object.details.student_names, name => {
              if (_.isString(name)) {
                return name
              }
            }).filter(element => {
              return element != null
            })

            if (_.isEmpty(studentNames)) {
              console.log("a notification object was passed with no valid student names")
              return "Something went wrong!"
            }

            return (
              studentNames.join(", ") + " are only assigned to each quest once with their classes"
            )
          }
        default:
          console.log(
            "a notification object was passed with an notification type that has not been implemented yet."
          )
          return "Something went wrong!"
      }
    }
  })
  return notificationArray
}

const buildStudentGroupings = studentGroupings => {
  const sections = _.map(studentGroupings, course => ({
    text: course.name,
    children: _.map(course.sections, section => {
      if (!_.isEmpty(section.students)) return { id: "section" + section.id, text: section.name }
    }).filter(x => x !== undefined)
  }))
  const students = _.flatMap(studentGroupings, course =>
    _.map(course.sections, section => {
      if (!_.isEmpty(section.students))
        return {
          text: section.name,
          children: _.map(section.students, student => ({
            id: "student" + student.id,
            text:
              student.profile.firstName +
              " " +
              (student.profile.lastName ? student.profile.lastName : "")
          }))
        }
    })
  )
  let assigneeOptions = [{ text: "", children: [{ id: "myself", text: "Myself" }] }]
  if (!_.isEmpty(sections)) {
    assigneeOptions.push(sections.filter(x => x !== undefined))
  }
  if (!_.isEmpty(students)) {
    assigneeOptions.push(students.filter(x => x !== undefined))
  }
  return _.flatten(assigneeOptions)
}

//User role helper functions
export const EDUCATOR = "EDUCATOR"
export const HOMESCHOOLER = "HOMESCHOOLER"
export const PARENT = "PARENT"
export const STUDENT = "STUDENT"
export const CHILD = "CHILD"

const getCurrentRole = currentUser => {
  if (!_.isEmpty(currentUser)) {
    return currentUser["is-educator"]
      ? "EDUCATOR"
      : currentUser["is-homeschooler"]
      ? "HOMESCHOOLER"
      : currentUser["is-parent"]
      ? "PARENT"
      : currentUser["is-student"]
      ? "STUDENT"
      : "CHILD"
  }
  return null
}

//Sandbox helper functions
const getSandboxName = (sandboxName, sandboxQuestName) => {
  return `Introduction to ${sandboxName}: ${sandboxQuestName}`
}

const getSandboxQuestDescription = (sandboxDescription, sandboxName) => {
  return (
    <React.Fragment>
      {sandboxDescription}
      <br />
      <br />
      {`The student will gain permanent access to ${sandboxName} sandbox after the completion of this quest.`}
    </React.Fragment>
  )
}

export const crudTypes = {
  INDEX: Symbol("INDEX"),
  CREATE: Symbol("CREATE"),
  READ: Symbol("READ"),
  UPDATE: Symbol("UPDATE"),
  DELETE: Symbol("DELETE")
}

//interaction related helper functions
const handleClickOutside = (eventName, ref, callback) => {
  document.addEventListener(eventName, e => {
    if (ref.current && !ref.current.contains(e.target)) {
      callback()
    }
  })
}

export {
  getURLParams,
  isDevEnvironment,
  isPlural,
  alphabetizeByKey,
  transformKeys,
  utcISOStringToLocalISOString,
  executeIfNotEqual,
  toggleValueInList,
  mod,
  objectArrayToObject,
  getDayArray,
  formatKeysToCamelCase,
  formatStudentKeysToCamelCase,
  defineQuestTypes,
  getNotificationMessages,
  buildStudentGroupings,
  getNumericDateString,
  getCurrentRole,
  getSandboxName,
  getSandboxQuestDescription,
  handleClickOutside
}
